import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';
import { ReactNodeViewRenderer } from '@tiptap/react';
import QuestionSetNodeView from './question-set-node-view';
import { createQuestionSetPasteFilterPlugin } from './question-set-paste-filter-plugin';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        questionSet: {
            addQuestionSetQuestion: (node?: PMNode) => ReturnType;
        };
    }
}
export interface QuestionSetOptions {
    multipleChoice?: boolean;
    shortAnswer?: boolean;
    matching?: boolean;
    detectAnswer?: boolean;
}

/**
 * Matches the first activity child in document order (same rules as addQuestionSetQuestion).
 */
export function inferQuestionTypeFromQuestionSetChildren(node: PMNode): string | null {
    for (let i = 0; i < node.childCount; i++) {
        const child = node.child(i);
        if (child.type.name === 'multipleChoice') {
            return 'multipleChoice';
        }
        if (child.type.name === 'shortAnswer') {
            return 'shortAnswer';
        }
        if (child.type.name === 'matching') {
            return 'matching';
        }
        if (child.type.name === 'detectAnswer') {
            return 'detectAnswer';
        }
    }
    return null;
}

export const QuestionSetHeadless = Node.create<QuestionSetOptions>({
    name: 'questionSet',

    addOptions() {
        return {
            multipleChoice: true,
            shortAnswer: true,
            matching: true,
            detectAnswer: true,
        };
    },

    content: function () {
        const schemas = [];
        if (this.options.multipleChoice) schemas.push(`(${getBasicContentSchema()} multipleChoice+)`);
        if (this.options.shortAnswer) schemas.push(`(${getBasicContentSchema()} shortAnswer+)`);
        if (this.options.matching) schemas.push(`(${getBasicContentSchema()} matching+)`);
        if (this.options.detectAnswer) schemas.push(`(${getBasicContentSchema()} detectAnswer+)`);
        return schemas.join(' | ');
    },

    group: 'block',

    draggable: true,

    selectable: true,

    defining: true,

    isolating: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('data-id');
                },
                renderHTML: attributes => {
                    if (!attributes.id) {
                        return {};
                    }
                    return {
                        'data-id': attributes.id,
                    };
                },
            },
            activityType: {
                default: 'participation',
                parseHTML: element => {
                    return element.getAttribute('data-activity-type') || 'participation';
                },
                renderHTML: attributes => {
                    if (!attributes.activityType) {
                        return {};
                    }
                    return {
                        'data-activity-type': attributes.activityType,
                    };
                },
            },
            questionType: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('data-question-type');
                },
                renderHTML: attributes => {
                    if (!attributes.questionType) {
                        return {};
                    }
                    return {
                        'data-question-type': attributes.questionType,
                    };
                },
            },
            caption: {
                default: '',
                parseHTML: element => {
                    return element.getAttribute('data-caption') || '';
                },
                renderHTML: attributes => {
                    if (!attributes.caption) {
                        return {};
                    }
                    return {
                        'data-caption': attributes.caption,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div.question-set',
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'div',
            {
                ...HTMLAttributes,
                class: 'question-set',
                'data-activity-type': node.attrs.activityType,
            },
            0,
        ];
    },

    addProseMirrorPlugins() {
        return [createQuestionSetPasteFilterPlugin()];
    },

    addCommands() {
        return {
            addQuestionSetQuestion:
                (questionSetNode?: PMNode) =>
                ({ state, chain }) => {
                    let questionSetNodePos = -1;

                    if (!questionSetNode) {
                        console.error('No question set node provided.');
                        return false;
                    }

                    // Find position of the provided node
                    state.doc.descendants((node, pos) => {
                        if (node.attrs.id === questionSetNode.attrs.id) {
                            questionSetNodePos = pos;
                            return false;
                        }
                    });

                    if (questionSetNodePos === -1) {
                        console.error('Question set node not found in document.');
                        return false;
                    }

                    let questionType: string | null = questionSetNode.attrs.questionType;

                    if (!questionType) {
                        questionType = inferQuestionTypeFromQuestionSetChildren(questionSetNode);
                    }

                    if (!questionType) {
                        return false;
                    }

                    // Calculate position at the end of the container node
                    const endPos = questionSetNodePos + questionSetNode.nodeSize - 1;

                    // Insert the appropriate activity type at the end of the container
                    switch (questionType) {
                        case 'multipleChoice':
                            return chain()
                                .focus()
                                .setTextSelection(endPos)
                                .insertBareMultipleChoice()
                                .selectDescendantNodeContent('multipleChoiceQuestion', endPos)
                                .run();
                        case 'shortAnswer':
                            return chain()
                                .focus()
                                .setTextSelection(endPos)
                                .insertBareShortAnswer()
                                .selectDescendantNodeContent('shortAnswerQuestion', endPos)
                                .run();
                        case 'matching':
                            return chain().focus().setTextSelection(endPos).insertBareMatching().run();
                        case 'detectAnswer':
                            return chain()
                                .focus()
                                .setTextSelection(endPos)
                                .insertBareDetectAnswer()
                                .selectDescendantNodeContent('detectAnswerQuestion', endPos)
                                .run();
                        default:
                            return false;
                    }
                },
        };
    },
});

export const QuestionSet = QuestionSetHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(QuestionSetNodeView, { trackNodeViewPosition: true });
    },
});

export default QuestionSet;
